import { type JSX, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { kopfSichern, type Kopfangaben, type Rechnung } from '../api/rechnungen';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/** Ein leeres Feld heißt „keine Angabe“ — die API kennt dafür `null`. */
function oderNull(wert: string): string | null {
  const t = wert.trim();
  return t === '' ? null : t;
}

function ganzeZahlOderNull(wert: string): number | null {
  const t = wert.trim();
  return t === '' ? null : Number(t);
}

/**
 * Die Angaben eines Rechnungsentwurfs bearbeiten (Auftrag CRM-119, L8).
 *
 * Texte und Zahlungsbedingungen wandern aus dem Auftrag mit (AUF-02) und
 * lassen sich vor dem Festschreiben noch anpassen. Preise und Mengen nicht:
 * Sie sind der Vertrag (AUF-01) und ändern sich nur am Auftrag. Das
 * Belegdatum setzt die Festschreibung (RNG-07), der Empfänger hat sein
 * eigenes Pop-up (KUN-08).
 *
 * Keine Rückfrage: Der Entwurf bleibt änderbar, jede Angabe lässt sich
 * zurücksetzen (Tabelle 29, ÄA-62 B). Einspaltig, auch auf dem Telefon
 * (ÄA-63) — geöffnet wird die Maske dort aber nicht, Bearbeiten ab Tablet.
 */
export function Angabenmaske({
  rechnung,
  beiGesichert,
  beiSchliessen,
}: {
  readonly rechnung: Rechnung;
  readonly beiGesichert: (meldung: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [betreff, setzeBetreff] = useState(rechnung.betreff);
  const [kopftext, setzeKopftext] = useState(rechnung.kopftext ?? '');
  const [fusstext, setzeFusstext] = useState(rechnung.fusstext ?? '');
  const [zahlungsziel, setzeZahlungsziel] = useState(String(rechnung.zahlungsziel));
  const [skontoProzent, setzeSkontoProzent] = useState(
    rechnung.skontoProzent === null ? '' : rechnung.skontoProzent.replace('.', ','),
  );
  const [skontoTage, setzeSkontoTage] = useState(
    rechnung.skontoTage === null ? '' : String(rechnung.skontoTage),
  );
  const [bestellreferenz, setzeBestellreferenz] = useState(rechnung.bestellreferenz ?? '');
  const [leitwegId, setzeLeitwegId] = useState(rechnung.leitwegId ?? '');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  /* Nur was sich geändert hat, geht hinaus — die Prüfung der API meldet dann genau das Feld. */
  const aenderungen = (): Kopfangaben => {
    const prozent = oderNull(skontoProzent)?.replace(',', '.') ?? null;
    const neu: Record<string, string | number | null> = {};
    if (betreff.trim() !== rechnung.betreff) neu['betreff'] = betreff.trim();
    if (oderNull(kopftext) !== rechnung.kopftext) neu['kopftext'] = oderNull(kopftext);
    if (oderNull(fusstext) !== rechnung.fusstext) neu['fusstext'] = oderNull(fusstext);
    if (Number(zahlungsziel) !== rechnung.zahlungsziel) neu['zahlungsziel'] = Number(zahlungsziel);
    if (prozent !== rechnung.skontoProzent) neu['skontoProzent'] = prozent;
    if (ganzeZahlOderNull(skontoTage) !== rechnung.skontoTage) {
      neu['skontoTage'] = ganzeZahlOderNull(skontoTage);
    }
    if (oderNull(bestellreferenz) !== rechnung.bestellreferenz) {
      neu['bestellreferenz'] = oderNull(bestellreferenz);
    }
    if (oderNull(leitwegId) !== rechnung.leitwegId) neu['leitwegId'] = oderNull(leitwegId);
    return neu as Kopfangaben;
  };
  const geaendert = Object.keys(aenderungen()).length > 0;
  const zielGueltig = /^\d{1,3}$/.test(zahlungsziel.trim());

  const sichern = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      // C9.2: `stand` — hat jemand anderes inzwischen gesichert, kommt 409.
      await kopfSichern(rechnung.id, aenderungen(), rechnung.stand);
      beiGesichert('Angaben der Rechnung gesichert.');
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  };

  const schliessen = (): void => {
    if (!laeuft) beiSchliessen();
  };

  return (
    <Dialog
      titel="Angaben bearbeiten"
      unterzeile="Texte und Zahlungsbedingungen des Entwurfs. Preise und Mengen ändern sich am Auftrag."
      breite="mittel"
      beiSchliessen={schliessen}
    >
      <>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-betreff">
            Betreff
          </label>
          <input
            id="rg-betreff"
            type="text"
            maxLength={200}
            value={betreff}
            onChange={(e) => setzeBetreff(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-kopftext">
            Kopftext
          </label>
          <textarea
            id="rg-kopftext"
            rows={3}
            maxLength={8000}
            value={kopftext}
            onChange={(e) => setzeKopftext(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-fusstext">
            Fußtext
          </label>
          <textarea
            id="rg-fusstext"
            rows={3}
            maxLength={8000}
            value={fusstext}
            onChange={(e) => setzeFusstext(e.target.value)}
          />
        </div>
        {/* AUF-02: aus dem Auftrag eingefroren; hier nur noch für diesen Beleg. */}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-ziel">
            Zahlungsziel in Tagen
          </label>
          <input
            id="rg-ziel"
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            value={zahlungsziel}
            onChange={(e) => setzeZahlungsziel(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-skonto">
            Skonto in Prozent
          </label>
          <input
            id="rg-skonto"
            type="text"
            inputMode="decimal"
            value={skontoProzent}
            onChange={(e) => setzeSkontoProzent(e.target.value)}
          />
          <span className="feld__hilfe">Leer heißt ohne Skonto.</span>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-skontotage">
            Skonto innerhalb von Tagen
          </label>
          <input
            id="rg-skontotage"
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            value={skontoTage}
            onChange={(e) => setzeSkontoTage(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-bestellreferenz">
            Bestellreferenz
          </label>
          <input
            id="rg-bestellreferenz"
            type="text"
            maxLength={80}
            value={bestellreferenz}
            onChange={(e) => setzeBestellreferenz(e.target.value)}
          />
        </div>
        {/* RNG-16, BT-10: Pflicht für öffentliche Auftraggeber. */}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="rg-leitweg">
            Leitweg-ID
          </label>
          <input
            id="rg-leitweg"
            type="text"
            maxLength={60}
            value={leitwegId}
            onChange={(e) => setzeLeitwegId(e.target.value)}
          />
        </div>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" disabled={laeuft} onClick={schliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft || !geaendert || betreff.trim() === '' || !zielGueltig}
            aria-busy={laeuft}
            onClick={() => void sichern()}
          >
            Sichern
          </button>
        </Abschlussreihe>
      </>
    </Dialog>
  );
}
